/**
 * Theme preference helpers.
 *
 * The Tailwind config uses `darkMode: ['class']`, so we toggle dark
 * mode by adding/removing the `dark` class on the document element.
 * `system` lets the OS-level preference take over via the
 * `prefers-color-scheme` media query.
 *
 * Persistence is localStorage-only — the backend Setting table has
 * no `user` scope (only global/company/vessel) and the preference is
 * device-local in practice. Callers (SettingsPage) write through
 * `setStoredTheme` and the function applies the class change in the
 * same tick so the UI updates immediately.
 */

export const THEMES = ['light', 'dark', 'system'] as const;
export type Theme = (typeof THEMES)[number];

const STORAGE_KEY = 'isi-gmao.preferredTheme';

export function getStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'system';
  const raw = window.localStorage.getItem(STORAGE_KEY);
  return (THEMES as readonly string[]).includes(raw ?? '')
    ? (raw as Theme)
    : 'system';
}

export function setStoredTheme(theme: Theme): void {
  if (typeof window === 'undefined') return;
  if (theme === 'system') {
    window.localStorage.removeItem(STORAGE_KEY);
  } else {
    window.localStorage.setItem(STORAGE_KEY, theme);
  }
  applyTheme(theme);
}

/**
 * Resolve the effective theme (light or dark) at this moment given
 * the user preference and the OS-level setting.
 *
 * `window.matchMedia` is missing in jsdom (and gated behind a flag
 * in some older browsers); we treat its absence as "light".
 */
function resolveEffective(theme: Theme): 'light' | 'dark' {
  if (theme !== 'system') return theme;
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

export function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  const effective = resolveEffective(theme);
  const root = document.documentElement;
  if (effective === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
}

/**
 * Apply the stored theme on bootstrap. Call once from main.tsx
 * before React mounts so the very first paint matches the user's
 * preference (no flash of light theme on a dark-mode user).
 */
export function bootstrapTheme(): void {
  applyTheme(getStoredTheme());
}
